"use client";

import { cn } from "@scott/ui/utils/cn";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Body,
  Facebook,
  Heading,
  Instagram,
  Pinterest,
  XIcon,
  Youtube,
} from "@ui-d2c/components/ui";
import type {
  AddressData,
  LinkItem,
  LinkType,
  SocialIconKey,
} from "@ui-d2c/types";
import { Plus } from "lucide-react";
import { useParams } from "next/navigation";
import { registrationAccessToDataLayer } from "@/utils/gtm.utils";

export interface footerLinksProps {
  footerLinks?: LinkType[];
  className?: string;
}

const socialIconsComponent: Record<
  SocialIconKey,
  { label: string; icon: React.ReactNode }
> = {
  f: {
    label: "Facebook",
    icon: (
      <Facebook className="fill-followus-logo hover:fill-followus-logo-hover" />
    ),
  },
  x: {
    label: "X",
    icon: (
      <XIcon className="fill-followus-logo hover:fill-followus-logo-hover" />
    ),
  },
  i: {
    label: "Instagram",
    icon: (
      <Instagram className="fill-followus-logo hover:fill-followus-logo-hover" />
    ),
  },
  y: {
    label: "YouTube",
    icon: (
      <Youtube className="fill-followus-logo hover:fill-followus-logo-hover" />
    ),
  },
  p: {
    label: "Pinterest",
    icon: (
      <Pinterest className="fill-followus-logo hover:fill-followus-logo-hover" />
    ),
  },
};

const FooterLinks = ({ className, footerLinks }: footerLinksProps) => {
  const params = useParams();
  const openCookieConsent = () => {
    if (window.UC_UI) {
      window.UC_UI.showSecondLayer();
    }
  };

  const renderFollowUsIcons = (icons: LinkItem[]) => (
    <div className="flex flex-wrap gap-4">
      {icons.map((icon, _index) =>
        icon.text ? (
          <a
            aria-label={socialIconsComponent[icon.text as SocialIconKey].label}
            href={icon.link}
            key={`item-footer-icon-${icon.text}-${icon.link}`}
          >
            {socialIconsComponent[icon.text as SocialIconKey].icon}
          </a>
        ) : null
      )}
    </div>
  );

  const renderAddresses = (addresses: AddressData[]) =>
    addresses.map((address, i) => (
      <div
        className="flex flex-col gap-2"
        key={`footer-address-${address.title}-${i}`}
      >
        <Heading
          className="text-nowrap text-footer-link tracking-normal"
          hasUnderline={true}
          variant={"h5"}
        >
          {address.title}
        </Heading>
        {address.links.map((link: LinkItem, i: number) => (
          <Body
            className="text-followus-normal"
            key={`item-footer-address-link-${link.text}-${i}`}
            size="sm"
          >
            {link.text}
          </Body>
        ))}
      </div>
    ));

  const handleGTMOnClickLinks = (href?: string) => {
    if (href?.includes("/support/registration")) {
      registrationAccessToDataLayer(
        params.country ?? "",
        params.lang ?? "",
        "Footer"
      );
    }
  };

  return (
    <div className="px-6 py-8">
      <div
        className={cn(
          "container hidden items-start gap-4 bg-transparent md:flex",
          className
        )}
      >
        {footerLinks?.map((l, i) => (
          <div
            className="flex flex-1 flex-col gap-4"
            key={`item-footer-group-${l.heading}-${i}`}
          >
            <div className="flex sm:gap-2">
              <Heading
                className="text-nowrap text-footer-link tracking-normal"
                hasUnderline={true}
                variant={"h5"}
              >
                {l.heading}
              </Heading>
            </div>
            {l.links?.map((l, i) =>
              // TODO: if link is empty, we need to open the cookie consent modal
              l.link ? (
                <a
                  href={l.link}
                  // @ts-expect-error - heading property not in LinkItem type
                  key={`footer-link-${l.heading}-${l.link}-${i}`}
                  onClick={() => handleGTMOnClickLinks(l.link)}
                >
                  <Body
                    className="text-followus-normal hover:text-followus-hover"
                    size="sm"
                  >
                    {l.text}
                  </Body>
                </a>
              ) : (
                <div
                  // @ts-expect-error - heading property not in LinkItem type
                  key={`cookieConsent-${l.heading}-${i}`}
                  onClick={openCookieConsent}
                >
                  <Body
                    className="cursor-pointer text-followus-normal hover:text-followus-hover"
                    size="sm"
                  >
                    {l.text}
                  </Body>
                </div>
              )
            )}
            <div className="flex flex-col gap-4 lg:gap-8">
              {l.followUsIcons && (
                <div className="flex flex-row flex-wrap gap-4">
                  {renderFollowUsIcons(l.followUsIcons)}
                </div>
              )}
              {l.addresses && renderAddresses(l.addresses)}
            </div>
          </div>
        ))}
      </div>
      <div
        className={cn(
          "visible flex h-auto flex-col gap-4 bg-transparent sm:container md:invisible md:h-0",
          className
        )}
      >
        <Accordion className="flex flex-col gap-4" collapsible type="single">
          {footerLinks?.map(
            (l, i) =>
              !l.followUsIcons && (
                <AccordionItem
                  className="flex flex-1 flex-col gap-4"
                  key={`${l.heading}-${i}`}
                  value={l.heading ? l.heading : `item-${i}`}
                >
                  <AccordionTrigger className="[&[data-state=open]>svg]:rotate-45">
                    <Heading
                      className="flex flex-1 basis-2/3 text-nowrap text-footer-link tracking-normal"
                      variant={"h5"}
                    >
                      {l.heading}
                    </Heading>
                    <Plus className="h-4 w-4 flex-1-0 text-neutral-lighter transition-transform duration-200" />
                  </AccordionTrigger>
                  <AccordionContent className="space-y-2">
                    {!l.followUsIcons &&
                      l.links &&
                      l.links.map((l, _i) => (
                        <a
                          href={l.link}
                          key={`accordion-link-${l.text}-${l.link}`}
                        >
                          <Body
                            className="text-nowrap text-footer-link leading-8 hover:text-brand-dark"
                            size="sm"
                          >
                            {l.text}
                          </Body>
                        </a>
                      ))}
                  </AccordionContent>
                </AccordionItem>
              )
          )}
        </Accordion>
        {footerLinks?.map(
          (l, i) =>
            l.followUsIcons && (
              <div
                className="flex flex-1 overflow:flex-col flex-wrap justify-center gap-4 py-4"
                key={`followus-section-${l.heading || i}`}
              >
                {renderFollowUsIcons(l.followUsIcons)}
              </div>
            )
        )}
        <Accordion
          className="flex w-full flex-col gap-4"
          collapsible
          type="single"
        >
          {footerLinks?.map((l) =>
            l.addresses?.map((address, i) => (
              <AccordionItem
                className="flex flex-1 flex-col gap-4"
                key={`address-${address.title || i}`}
                value={address.title}
              >
                <AccordionTrigger className="flex-row [&[data-state=open]>svg]:rotate-45">
                  <Heading
                    className="flex flex-1 basis-2/3 text-nowrap text-footer-link tracking-normal"
                    variant={"h5"}
                  >
                    {address.title}
                  </Heading>
                  <Plus className="h-4 w-4 flex-1-0 text-neutral-lighter transition-transform duration-200" />
                </AccordionTrigger>
                <AccordionContent className="space-y-2">
                  {address.links.map((link, i) => (
                    <Body
                      className="text-nowrap text-footer-link"
                      key={`address-link-${link.text}-${i}`}
                      size="sm"
                    >
                      {link.text}
                    </Body>
                  ))}
                </AccordionContent>
              </AccordionItem>
            ))
          )}
        </Accordion>
      </div>
    </div>
  );
};

FooterLinks.displayName = "footerLinks";

export { FooterLinks };
